import type { QueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { components } from '@/api/types.gen'

export type LotWithPart = components['schemas']['LotWithPart']
export type MovementResult = components['schemas']['MovementResult']
export type MovementKind = components['schemas']['MovementKind']

export function locationContentsKey(locationId: string, includeChildren: boolean) {
  return ['location', locationId, 'contents', includeChildren] as const
}

export async function fetchLocationContents(
  locationId: string,
  includeChildren: boolean,
): Promise<LotWithPart[]> {
  const { data } = await api.GET('/api/locations/{id}/contents', {
    params: { path: { id: locationId }, query: { include_children: includeChildren } },
  })
  return data ?? []
}

export type MovementBody = components['schemas']['MovementInput']

export async function applyMovement(lotId: string, body: MovementBody) {
  const { data } = await api.POST('/api/lots/{id}/movements', {
    params: { path: { id: lotId } },
    body,
  })
  return data as MovementResult
}

/** Everything that shows stock depends on more than one query; this is the
 * shared set to invalidate after anything changes it. The `part` prefix
 * already covers a part's movements. */
export async function invalidateStock(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['location'] }),
    queryClient.invalidateQueries({ queryKey: ['locations'] }),
    queryClient.invalidateQueries({ queryKey: ['parts'] }),
    queryClient.invalidateQueries({ queryKey: ['part'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['lots', 'recent'] }),
    queryClient.invalidateQueries({ queryKey: ['tree', 'locations'] }),
  ])
}

export type LotBody = components['schemas']['LotInput']

export async function createLot(partId: string, body: LotBody) {
  const { data } = await api.POST('/api/parts/{id}/lots', {
    params: { path: { id: partId } },
    body,
  })
  return data as MovementResult
}

export async function updateLot(lotId: string, body: LotBody) {
  await api.PUT('/api/lots/{id}', { params: { path: { id: lotId } }, body })
}

export async function deleteLot(lotId: string) {
  await api.DELETE('/api/lots/{id}', { params: { path: { id: lotId } } })
}

/** What each movement does, in the words the buttons use. */
export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  add: 'Add stock',
  withdraw: 'Take stock',
  set: 'Correct the count',
  move: 'Move stock',
}
